import React, { useEffect } from 'react';
import { 
  Container, 
  Typography, 
  Box, 
  Grid, 
  Paper, 
  Button, 
  Avatar, 
  Stack, 
  Divider 
} from '@mui/material';
import { 
  Groups as GroupsIcon, 
  Speed as SpeedIcon, 
  VerifiedUser as VerifiedIcon,
  HelpOutline as HelpIcon,
  EmojiObjects as MissionIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

const AboutPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const openHelp = () => document.dispatchEvent(new Event('krillou:open-help'));

  const values = [
                {
                    title: t('aboutPage.values.trust'),
                    desc: t('aboutPage.values.trustDesc'),
                    icon: (
                    <Box
                        sx={{
                        backgroundColor: 'primary.main',
                        color: 'white',
                        borderRadius: '50%',
                        width: 64,
                        height: 64,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: 3
                        }}
                    >
                        <VerifiedIcon fontSize="large" />
                    </Box>
                    )
                },
                {
                    title: t('aboutPage.values.reliability'),
                    desc: t('aboutPage.values.reliabilityDesc'),
                    icon: (
                    <Box sx={{
                        backgroundColor: 'primary.main',
                        color: 'white',
                        borderRadius: '50%',
                        width: 64,
                        height: 64,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: 3
                    }}>
                        <SpeedIcon fontSize="large" />
                    </Box>
                    )
                },
                {
                    title: t('aboutPage.values.hybrid'),
                    desc: t('aboutPage.values.hybridDesc'),
                    icon: (
                    <Box sx={{
                        backgroundColor: 'primary.main',
                        color: 'white',
                        borderRadius: '50%',
                        width: 64,
                        height: 64,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: 3
                    }}>
                        <GroupsIcon fontSize="large" />
                    </Box>
                    )
                }
                ];

  return (
    <Box sx={{ bgcolor: 'background.paper', pb: 10 }}>
      {/* Hero Section */}
      <Box 
        sx={{ 
          bgcolor: 'primary.main', 
          color: 'primary.contrastText', 
          py: { xs: 8, md: 12 },
          textAlign: 'center',
          mb: 8
        }}
      >
        <Container maxWidth="md">
          <Typography variant="h2" component="h1" gutterBottom fontWeight="bold">
            {t('aboutPage.heroTitle')}
          </Typography>
          <Typography variant="h5" sx={{ mb: 4, opacity: 0.9 }}>
            {t('aboutPage.heroSubtitle')}
          </Typography>
          <Button 
            variant="contained" 
            color="success"
            size="large" 
            onClick={() => navigate('/how-it-works')}
            sx={{ px: 4, py: 1.5, borderRadius: 2, color: 'white',border: '2px solid white' }}          
          >
            {t('navigation.howItWorks')}
          </Button>
        </Container>
      </Box>

      <Container maxWidth="lg">
        {/* Mission Section */}
        <Box sx={{ mb: 12 }}>
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <MissionIcon color="primary" sx={{ mr: 2 }} />
                <Typography variant="h4" fontWeight="bold">
                  {t('aboutPage.missionTitle')}
                </Typography>
              </Box>
              <Typography variant="body1" color="text.secondary" paragraph sx={{ fontSize: '1.1rem' }}>
                {t('aboutPage.missionDesc')}
              </Typography>
              <Typography variant="body1" color="text.secondary">
                {t('howItWorksPage.description')}
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper elevation={4} sx={{ p: 4, bgcolor: 'primary.main', color: 'primary.contrastText', borderRadius: 4 }}>
                <Typography variant="h5" gutterBottom fontWeight="bold">
                  Krillou
                </Typography>
                <Typography variant="body1" sx={{ opacity: 0.9 }}>
                  {t('app.description')}
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Box>

        <Divider sx={{ mb: 12 }} />

        {/* Values Grid */}
        <Box sx={{ mb: 12 }}>
          <Grid container spacing={4}>
            {values.map((v, i) => (
              <Grid item xs={12} md={4} key={i}>
                <Paper sx={{ p: 4, height: '100%', textAlign: 'center', borderRadius: 3,boxShadow: 3 }}>
                  <Avatar sx={{ bgcolor: 'primary.light', width: 60, height: 60, mx: 'auto', mb: 3 }}>
                    {v.icon}
                  </Avatar>
                  <Typography variant="h6" gutterBottom fontWeight="bold">
                    {v.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {v.desc}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Help CTA */}
        <Box 
          sx={{ 
            bgcolor: 'primary.main', 
            color: 'white', 
            p: { xs: 4, md: 6 }, 
            borderRadius: 4, 
            textAlign: 'center' 
          }}
        >
          <Typography variant="h4" gutterBottom fontWeight="bold">
            {t('howItWorksPage.helpTitle')}
          </Typography>
          <Typography variant="body1" sx={{ mb: 4, opacity: 0.9 }}>
            {t('howItWorksPage.helpDesc')}
          </Typography>
          <Button 
            variant="contained" 
            color="secondary" 
            size="large" 
            onClick={openHelp}
            startIcon={<HelpIcon />}
            sx={{ borderRadius: 2, px: 6 }}
          >
            {t('howItWorksPage.ctaHelp')}
          </Button>
        </Box>
      </Container>
    </Box>
  );
};

export default AboutPage;